import { useSyncExternalStore } from 'react';

/**
 * Minimal transient-toast store (the add-in has no toast component otherwise).
 * Module-scope pub/sub so the NotificationProvider can fire a toast
 * imperatively from its SSE handler. Auto-dismisses; max 3 stacked.
 */
export interface ToastData {
  id: string;
  title: string;
  message?: string;
  /** Optional action (e.g. "View" → open the notification panel). */
  onClick?: () => void;
}

const AUTO_DISMISS_MS = 6000;
const MAX_TOASTS = 3;

let toasts: ToastData[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function pushToast(t: Omit<ToastData, 'id'>): void {
  const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  toasts = [...toasts, { ...t, id }].slice(-MAX_TOASTS);
  emit();
  setTimeout(() => dismissToast(id), AUTO_DISMISS_MS);
}

export function dismissToast(id: string): void {
  const next = toasts.filter((t) => t.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): ToastData[] {
  return toasts;
}

/** React hook for the ToastHost to render the current toasts. */
export function useToasts(): ToastData[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
